import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Create a stable axios instance that doesn't change on every render
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: `${API_URL}/api`,
    });
    return instance;
  }, []);

  // Update auth header via interceptor when token changes
  useEffect(() => {
    const interceptorId = api.interceptors.request.use((config) => {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        delete config.headers.Authorization;
      }
      return config;
    });

    return () => {
      api.interceptors.request.eject(interceptorId);
    };
  }, [token, api]);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token, api]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    // Track successful login count per user (used by the PWA install banner
    // to wait until the second login before prompting). Stored as
    // `sc365_login_count_{userId}`. Capped at 99.
    try {
      if (userData?.id) {
        const k = `sc365_login_count_${userData.id}`;
        const next = Math.min(99, Number(localStorage.getItem(k) || 0) + 1);
        localStorage.setItem(k, String(next));
      }
    } catch { /* ignore quota errors */ }
    return userData;
  };

  const register = async (fullName, email, phone, password, role, extra) => {
    const response = await api.post('/auth/register', {
      fullName,
      email,
      phone,
      password,
      role,
      ...(extra || {}),
    });
    // Email verification flow: backend no longer issues a token on register.
    // It returns { user, emailVerificationRequired: true }. Caller must
    // route to /check-email instead of trying to log the user in.
    const { token: newToken, user: userData } = response.data;
    if (newToken) {
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
    }
    return response.data;
  };

  // Used by VerifyEmailPage after a successful /auth/verify-email — sets the
  // session in-memory so the user lands directly on onboarding.
  const setSessionFromVerification = (newToken, userData) => {
    if (!newToken) return;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshUser,
    setSessionFromVerification,
    isAuthenticated: !!user,
    isManager: user?.role === 'manager',
    isWorker: user?.role === 'worker',
    api
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
